/**
 * League Helper Utilities
 * Functions for checking event eligibility and league-related operations
 */

/**
 * Check if an event is eligible for a specific league
 * @param {string|number} eventId - The event ID to check
 * @param {Object} league - The league object with eventIds, startDate, and endDate
 * @param {string} eventDate - Optional event date for fallback date range check
 * @returns {boolean} - True if event is eligible for the league
 */
export const isEventEligibleForLeague = (eventId, league, eventDate = null) => {
  // NEW LEAGUES: Use eventIds if available and populated
  if (
    league.eventIds &&
    Array.isArray(league.eventIds) &&
    league.eventIds.length > 0
  ) {
    // Convert both to strings for comparison to handle type mismatches
    const eventIdStr = String(eventId);
    return league.eventIds.some((id) => String(id) === eventIdStr);
  }

  // OLD LEAGUES: Fallback to date range check if eventDate is provided
  if (eventDate && league.startDate && league.endDate) {
    const event = new Date(eventDate);
    const start = new Date(league.startDate);
    const end = new Date(league.endDate);
    return event >= start && event <= end;
  }

  // If no eventIds and no date info, assume eligible (for backward compatibility)
  return true;
};

/**
 * Filter leagues to only those where the event is eligible
 * @param {string|number} eventId - The event ID
 * @param {string} eventDate - The event date
 * @param {Array} leagues - Array of league objects
 * @returns {Array} - Filtered array of eligible leagues
 */
export const getEligibleLeagues = (eventId, eventDate, leagues) => {
  if (!leagues || !Array.isArray(leagues)) {
    return [];
  }

  return leagues.filter((league) =>
    isEventEligibleForLeague(eventId, league, eventDate),
  );
};

/**
 * Get league progress based on completed events
 * @param {Object} league - The league object with eventIds
 * @param {Array} completedEventIds - Array of completed event IDs
 * @returns {Object} - Progress object with completed, total, and percentage
 */
export const getLeagueProgress = (league, completedEventIds = []) => {
  if (
    !league.eventIds ||
    !Array.isArray(league.eventIds) ||
    league.eventIds.length === 0
  ) {
    return { completed: 0, total: 0, percentage: 0 };
  }

  const total = league.eventIds.length;
  const completed = league.eventIds.filter((eventId) =>
    completedEventIds.some(
      (completedId) => String(completedId) === String(eventId),
    ),
  ).length;

  return {
    completed,
    total,
    percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
  };
};

/**
 * Check if a league uses the new eventIds system or old date range system
 * @param {Object} league - The league object
 * @returns {boolean} - True if league uses eventIds system
 */
export const usesEventIdsSystem = (league) => {
  return !!(
    league.eventIds &&
    Array.isArray(league.eventIds) &&
    league.eventIds.length > 0
  );
};

/**
 * Get a user-friendly message explaining why an event is not eligible
 * @param {Object} league - The league object
 * @param {string} eventDate - The event date
 * @returns {string} - User-friendly message
 */
export const getIneligibilityReason = (league, eventDate) => {
  if (usesEventIdsSystem(league)) {
    return `This event is not part of ${league.leagueName || "this league"}. Only ${league.eventIds.length} specific events were selected when the league was created.`;
  }

  if (eventDate && league.startDate && league.endDate) {
    const event = new Date(eventDate);
    const start = new Date(league.startDate);
    const end = new Date(league.endDate);

    if (event < start) {
      return `This event occurs before the league start date (${start.toLocaleDateString()}).`;
    }
    if (event > end) {
      return `This event occurs after the league end date (${end.toLocaleDateString()}).`;
    }
  }

  return "This event is not eligible for this league.";
};
